import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { JournalCatalogForm } from "@/components/catalog/JournalCatalogForm";
import { JournalIdentifierStep } from "@/components/catalog/JournalIdentifierStep";
import { JournalMatchPanel } from "@/components/catalog/JournalMatchPanel";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useToast } from "@/contexts/ToastContext";
import { isValidIssn } from "@/lib/isbn";
import { catalogApi, type CatalogMatchResult } from "@/lib/api/catalog";
import { EMPTY_JOURNAL_STATE, type JournalCatalogState } from "./journal-catalog-form";
import { catalogErrorMessage } from "./journal-catalog-error";
import { useJournalCatalogOptions } from "./useJournalCatalogOptions";
import { useJournalCatalogWorkflow } from "./useJournalCatalogWorkflow";
import { useJournalDraftLoader } from "./useJournalDraftLoader";

type EntryStep = "identifier" | "form" | "match";

export function JournalCatalogPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const options = useJournalCatalogOptions();
  const draft = useJournalDraftLoader(id, toast);
  const [state, setState] = useState<JournalCatalogState>(EMPTY_JOURNAL_STATE);
  const [step, setStep] = useState<EntryStep>(id ? "form" : "identifier");
  const [match, setMatch] = useState<CatalogMatchResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const workflow = useJournalCatalogWorkflow({
    id,
    state,
    settings: options.settings,
    publicationStatus: draft.publicationStatus,
    navigate,
    toast,
    onSaved: () => setDirty(false),
  });

  useEffect(() => {
    if (!draft.loadedState) return;
    setState(draft.loadedState);
    setStep("form");
    setDirty(false);
  }, [draft.loadedState]);

  const changeState = (next: JournalCatalogState) => {
    setState(next);
    setDirty(true);
  };
  const cleanIssn = state.biblio.issn?.replace(/[-\s]/g, "").toUpperCase() ?? "";
  const continueFromIdentifier = async () => {
    if (!isValidIssn(cleanIssn) || checking) return;
    setChecking(true);
    try {
      const result = await catalogApi.matchCatalog({ issn: cleanIssn });
      setMatch(result);
      changeState({ ...state, biblio: { ...state.biblio, issn: cleanIssn } });
      setStep(result.matchType === "issn" && result.biblioId ? "match" : "form");
    } catch (error: unknown) {
      toast.error(catalogErrorMessage(error, "Failed to check for existing catalog records."));
    } finally {
      setChecking(false);
    }
  };
  const cancel = () => {
    if (dirty) setConfirmDiscard(true);
    else navigate("/catalog");
  };

  if (options.loading || draft.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  return (
    <div className="space-y-6 pb-16">
      <PageHeader
        title={id ? "Resume Journal Draft" : "Create Journal"}
        description="Create structured journal volumes, issues, and copies in one workflow."
        breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: "Journal" }]}
        showSearch={false}
      />
      {step === "identifier" ? (
        <JournalIdentifierStep
          identifiers={state.biblio}
          onChange={(identifiers) =>
            changeState({ ...state, biblio: { ...state.biblio, ...identifiers } })
          }
          onContinue={continueFromIdentifier}
          valid={isValidIssn(cleanIssn)}
          loading={checking}
        />
      ) : step === "match" && match ? (
        <JournalMatchPanel
          match={match}
          onReceiveIssue={() => navigate(`/catalog/journal/${match.biblioId}`)}
          onCancel={cancel}
        />
      ) : (
        <JournalCatalogForm
          state={state}
          onChange={changeState}
          errors={workflow.errors}
          options={options}
          submitting={workflow.submitting}
          publicationStatus={draft.publicationStatus}
          onAction={workflow.run}
          onCancel={cancel}
        />
      )}
      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard Changes?"
        description="You have unsaved journal changes."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => navigate("/catalog")}
      />
    </div>
  );
}
